import {
  computed,
  inject,
  onBeforeUnmount,
  onMounted,
  useAttrs,
  watch,
  type ComputedRef,
} from 'vue'
import { usePipe, type ComponentProcessorHandler } from './usePipe'

/**
 * Register component-provided processor handlers for the current component
 * instance. Other components on the pipe can invoke these via the
 * `component_processor` stock event processor.
 *
 * The instance key composes the component's `nodeId` with any SharedNodeRef
 * ancestor ids accumulated through the `sharedNodeRefAncestry` inject chain
 * (see [SharedNodeRefRenderer.vue](../SharedNodeRefRenderer.vue)). This lets
 * the same shared-template node register under distinct keys per live
 * instance, so two SharedNodeRefs embedding the same Timer template don't
 * collide.
 *
 * The nodeId is read from the component's fall-through `data-id` attribute —
 * `RenderComponents` sets this on every rendered component (see
 * [RenderComponents.vue:45](../RenderComponents.vue)). Components do NOT need
 * to declare any extra prop; they just call this composable with their
 * processor handlers.
 *
 * Re-registers when the instance key changes — this happens during
 * page-to-page navigation when the host SharedNodeRefRenderer is reused
 * (Vue keys SNRs by template id, not nodeId, so the same renderer instance
 * picks up the new page's SNR nodeId). Without re-registration, the old
 * key would stay in the pipe registry and the live instance would be
 * unreachable from the new page.
 *
 * Usage (in a component's `<script setup>`):
 * ```ts
 * useComponentProcessors({
 *   start: ({ config }) => startTimer(config.duration ?? 1000),
 *   stop: () => stopTimer(),
 * })
 * ```
 *
 * Declaration side — the component's `definition.ts` `processors` map tells
 * the editor what's available; this composable registers the actual
 * implementations at runtime. The two must agree on processor names.
 */
export function useComponentProcessors(handlers: Record<string, ComponentProcessorHandler>) {
  const pipe = usePipe()
  const ancestry = inject<ComputedRef<readonly string[]>>(
    'sharedNodeRefAncestry',
    computed(() => []),
  )
  // `data-id` is set by RenderComponents as a fall-through DOM attr; it's
  // available in $attrs regardless of which props the component declares.
  const attrs = useAttrs()

  const instanceKey = computed<string | null>(() => {
    const nodeId = attrs['data-id'] as string | undefined
    if (!nodeId) return null
    return [...ancestry.value, nodeId].join('/')
  })

  // Track the actually-registered key so unregister always targets it,
  // even if the computed has since recomputed to a different value.
  let lastKey: string | null = null

  onMounted(() => {
    const key = instanceKey.value
    if (!key) {
      console.warn(
        '[useComponentProcessors] data-id attr not found on component — ' +
          'cannot register processors. Is this component rendered via RenderComponents?',
      )
      return
    }
    pipe.registerComponentProcessors(key, handlers)
    lastKey = key
  })

  // Re-register when ancestry changes (host SNR was reused with new props).
  watch(instanceKey, (newKey) => {
    if (newKey === lastKey) return
    if (lastKey) pipe.unregisterComponentProcessors(lastKey)
    if (newKey) pipe.registerComponentProcessors(newKey, handlers)
    lastKey = newKey
  })

  onBeforeUnmount(() => {
    if (lastKey) pipe.unregisterComponentProcessors(lastKey)
  })
}
